import {css, Typography, useTheme} from '@mui/material';
import {ConfirmDialog, DlMuiDynamicIcon, ExportUtils, GetStylesFunction, useApiQuery, useCurrentLocale} from '@wallaby-ui/core';
import {useFormatters} from '@wallaby-ui/document';
import {useSnackbar} from 'notistack';
import * as React from 'react';
import {useCallback, useState} from 'react';

type Options = {
  /** MERIE's `GET .../xlsx` of the page. */
  url: string;
  /** The instance's export limit; left out on a page whose file has no limit (the board, PDW-413): no warning then. */
  maxExportRowsUrl?: string;
  /** The page's filter and sort, no page: the file holds the page's order up to the export limit. */
  params: URLSearchParams;
  /** What the limit counts on this page: the page's total, compared with the instance's export limit. */
  totalCount?: number;
  /** The limit dialog's texts, by default the common ones that speak of rows. */
  limitTitleKey?: string;
  limitTextKey?: string;
};

/**
 * Export data of an alert page (PDW-237 / FE PDW-253): the page's query sent to MERIE's xlsx endpoint, which answers
 * the file, its name and the limit flag. Above the instance's export limit a dialog warns first that the file will not
 * be complete. A failed export says so, since a missing file looks like a click that did nothing.
 */
export function useAlertExport(options: Options) {
  const {url, maxExportRowsUrl, params, totalCount, limitTitleKey, limitTextKey} = options;
  const currentLocale = useCurrentLocale();
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const {t} = useFormatters();
  const snackBar = useSnackbar();

  const {data: maxExportedRows} = useApiQuery<number | undefined>([maxExportRowsUrl], null, {
    axiosConfig: {url: maxExportRowsUrl},
    reactQueryOptions: {enabled: Boolean(maxExportRowsUrl)},
  });

  // one export at a time: the file can take a while to build and a second click would only start another
  const [isExporting, setIsExporting] = useState(false);
  const downloadXlsxExport = useCallback(() => {
    setIsExporting(true);
    return ExportUtils.handleExportXlsx({url, filters: Object.fromEntries(params), locale: currentLocale})
      .catch(() => {
        snackBar.enqueueSnackbar(t('common.export.failed'), {variant: 'error'});
      })
      .finally(() => setIsExporting(false));
  }, [url, params, currentLocale, snackBar, t]);

  const overLimit = Boolean(maxExportRowsUrl && maxExportedRows) && totalCount != null && totalCount > (maxExportedRows as number);
  const [confirmOpened, setConfirmOpened] = useState(false);
  const handleXlsxExport = useCallback(() => {
    if (overLimit) setConfirmOpened(true);
    else downloadXlsxExport();
  }, [overLimit, downloadXlsxExport]);
  const onConfirm = useCallback(() => {
    setConfirmOpened(false);
    downloadXlsxExport();
  }, [downloadXlsxExport]);

  const warningDialog = overLimit ? (
    <ConfirmDialog
      isOpened={confirmOpened}
      handleConfirm={onConfirm}
      handleCancel={() => setConfirmOpened(false)}
      showCancel={false}
      titleIcon={
        <DlMuiDynamicIcon
          iconName='haidiWarningIcon'
          useFixedViewBox='0 0 13 12'
          css={muiStyles.confirmIconButton}
          svgIconInputCss={muiStyles.confirmIcon}
          noButtonStyles
        />
      }
      titleText={t(limitTitleKey ?? 'common.export.confirmDialogLimit.title')}
      confirmText={t('common.confirmDialog.accept')}
    >
      <Typography variant='value'>{t(limitTextKey ?? 'common.export.confirmDialogLimit.text')}</Typography>
    </ConfirmDialog>
  ) : undefined;

  return {handleXlsxExport, isExporting, warningDialog};
}

const getStyles: GetStylesFunction = () => ({
  confirmIconButton: css`
    color: var(--dlTheme-500);
    padding: 0;
  `,
  confirmIcon: css`
    font-size: 20px;
  `,
});
